/**
 * 셈핏 클라이언트 사이드 암호화
 * ─────────────────────────────────────────────────────
 * · AES-GCM 256-bit (Web Crypto API — 브라우저 내장)
 * · 키 유도: PBKDF2(userId + APP_PEPPER, salt, 100_000회, SHA-256)
 * · userId마다 고유한 키 → 개발자가 Firestore를 열어도 암호문만 보임
 * · 암호화된 값은 "ENC::" 접두사로 구별 → 기존 미암호화 데이터와 호환
 * ─────────────────────────────────────────────────────
 * ※ APP_PEPPER가 번들에 포함되지만, 이것만으로는 복호화 불가
 *    (각 사용자의 userId도 함께 있어야 해당 사용자의 키 유도 가능)
 */

const APP_PEPPER = 'ddabak_enc_v1_2025_secure';
const KEY_CACHE  = new Map(); // 세션 내 키 캐시 (PBKDF2는 느리므로 1회만 계산)

/** PBKDF2로 AES-GCM 키 유도 (userId당 1회 계산 후 캐시) */
async function deriveKey(userId) {
  if (KEY_CACHE.has(userId)) return KEY_CACHE.get(userId);

  const enc  = new TextEncoder();
  const base = await crypto.subtle.importKey(
    'raw',
    enc.encode(userId + APP_PEPPER),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  // 결정론적 salt (userId 기반, 고정)
  const saltSrc = (userId + 'ddabak').slice(0, 16).padEnd(16, '0');
  const salt    = enc.encode(saltSrc);

  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 100_000, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );

  KEY_CACHE.set(userId, key);
  return key;
}

/**
 * 데이터(object/array) → 암호화된 Base64 문자열
 * Firestore에 저장하는 값
 */
export async function encryptField(userId, data) {
  const key = await deriveKey(userId);
  const iv  = crypto.getRandomValues(new Uint8Array(12)); // GCM 권장 12바이트 nonce
  const encoded  = new TextEncoder().encode(JSON.stringify(data));
  const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

  // iv(12) + ciphertext 를 합쳐 Base64 인코딩
  const combined = new Uint8Array(12 + cipherBuf.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(cipherBuf), 12);

  return 'ENC::' + btoa(String.fromCharCode(...combined));
}

/**
 * 암호화된 Base64 문자열 → 원본 데이터
 * "ENC::" 접두사가 없으면 기존 평문 데이터 → null 반환 (호환성)
 */
export async function decryptField(userId, str) {
  if (!str || typeof str !== 'string' || !str.startsWith('ENC::')) return null;

  try {
    const key      = await deriveKey(userId);
    const combined = Uint8Array.from(atob(str.slice(5)), c => c.charCodeAt(0));
    const iv       = combined.slice(0, 12);
    const cipher   = combined.slice(12);
    const plain    = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher);
    return JSON.parse(new TextDecoder().decode(plain));
  } catch (e) {
    console.error('복호화 실패 (데이터 손상 또는 키 불일치):', e);
    return null;
  }
}

/** 키 캐시 초기화 (로그아웃 시 호출) */
export function clearKeyCache() {
  KEY_CACHE.clear();
}
